import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import type { EstadoOrden, MotivoCancelacion } from '@/types/domain';
import {
  parseIntegerField,
  parseMoneyField,
  type GuideFileState,
  type GuideFormState,
  type OrderFormState,
  type OrderItemRow,
} from './orders-payload';

/**
 * Validación en cliente de los formularios de Órdenes (módulo 7).
 *
 * Espejo de `backend/src/modules/orders/orders.schemas.ts`: los topes son los mismos que aplica el
 * backend, para que el usuario vea el error al instante en vez de después de un viaje al servidor.
 * El backend sigue validando (responde 400 con el detalle de Zod, que la pantalla reparte por
 * campo con `formErrorsFromApiError`): esto es comodidad, no la única barrera.
 *
 * Dos reglas son de captura, no del schema, porque un pedido a medio llenar no se puede despachar:
 *  - tiene que haber al menos una línea con nombre y cantidad;
 *  - una línea empezada tiene que estar completa (nombre y cantidad entera ≥ 1).
 *
 * El `motivo` del cambio de estado es opcional (el schema solo lo limita a 500 caracteres), pero la
 * pantalla lo pide al cancelar: es el único dato que explica la cancelación en el historial.
 */

/**
 * Topes del contrato (`orders.schemas.ts`), en un solo sitio para poder citarlos en la UI.
 *
 * `guiaFotoBytes` no es un tope de Zod: lo aplica el almacenamiento local del backend (5 MB, el
 * mismo `MAX_IMAGE_BYTES` que usa el módulo de visión). La pantalla lo comprueba ANTES de leer el
 * archivo, para no convertir a base64 una foto que el backend va a rechazar.
 */
export const ORDER_LIMITS = {
  clienteNombre: 200,
  items: 50,
  itemNombre: 200,
  itemCantidad: 100000,
  direccion: 300,
  zona: 120,
  telefono: 40,
  metodoPago: 120,
  motivo: 500,
  guiaNumero: 120,
  guiaFotoBytes: 5 * 1024 * 1024,
} as const;

function tooLong(value: string, max: number, label: string): string | undefined {
  return value.trim().length > max ? `${label} no puede superar los ${max} caracteres` : undefined;
}

/** `clienteNombre` — obligatorio y hasta 200 caracteres (mismo mensaje que el backend). */
export function validateClientName(value: string): string | undefined {
  if (value.trim().length === 0) return 'El nombre del cliente es obligatorio';
  return tooLong(value, ORDER_LIMITS.clienteNombre, 'El nombre del cliente');
}

/** `telefono` — opcional, hasta 40 caracteres. */
export function validatePhone(value: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.telefono, 'El teléfono');
}

/** `direccion` — opcional, hasta 300 caracteres. */
export function validateAddress(value: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.direccion, 'La dirección');
}

/** `departamento` / `ciudad` / `barrio` — opcionales, hasta 120 caracteres cada uno. */
export function validateZonePart(value: string, label: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.zona, label);
}

/** `metodoPago` — opcional, hasta 120 caracteres. */
export function validatePaymentMethod(value: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.metodoPago, 'El método de pago');
}

/** `total` — obligatorio y ≥ 0 (el backend lo exige; el diálogo lo calcula de los productos). */
export function validateTotal(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return 'El total es obligatorio';

  const parsed = parseMoneyField(text);
  if (parsed === null) return 'El total debe ser un número (solo dígitos, con punto o coma decimal)';
  if (parsed < 0) return 'El total no puede ser negativo';
  return undefined;
}

/**
 * `productos` — al menos una línea con nombre, y hasta 50.
 *
 * Las líneas sin nombre no son productos: `orderItemPayloads` las descarta, así que aquí se ignoran
 * (una fila con precio pero sin nombre no puede viajar al backend). Si NO queda ninguna con nombre,
 * falta el pedido entero. La cantidad es un entero entre 1 y 100000, como el schema, y el precio
 * unitario es opcional pero, si se escribe, tiene que ser un número ≥ 0: el subtotal que viaja al
 * backend se calcula de él, así que un precio ilegible sería un pedido mal cobrado.
 */
export function validateItems(rows: OrderItemRow[]): string | undefined {
  const items = rows.filter((row) => row.nombre.trim().length > 0);

  if (items.length === 0) return 'La orden necesita al menos un producto';
  if (items.length > ORDER_LIMITS.items) {
    return `Una orden puede llevar hasta ${ORDER_LIMITS.items} productos`;
  }

  for (const row of items) {
    const nombre = row.nombre.trim();
    if (nombre.length > ORDER_LIMITS.itemNombre) {
      return `El nombre del producto no puede superar los ${ORDER_LIMITS.itemNombre} caracteres`;
    }

    const cantidad = parseIntegerField(row.cantidad);
    if (cantidad === null || cantidad < 1) {
      return 'La cantidad de cada producto debe ser un entero positivo';
    }
    if (cantidad > ORDER_LIMITS.itemCantidad) {
      return `La cantidad no puede pasar de ${ORDER_LIMITS.itemCantidad}`;
    }

    if (row.precioUnitario.trim().length === 0) continue;

    const precio = parseMoneyField(row.precioUnitario);
    if (precio === null) return 'El precio unitario debe ser un número (por ejemplo 25000)';
    if (precio < 0) return 'El precio unitario no puede ser negativo';
  }

  return undefined;
}

/** Validación completa del formulario de la orden manual, con la forma que consumen `Field` y `FormErrorAlert`. */
export function validateOrderForm(form: OrderFormState): FormErrors {
  return formErrorsFromFields({
    clienteNombre: validateClientName(form.clienteNombre),
    productos: validateItems(form.items),
    total: validateTotal(form.total),
    telefono: validatePhone(form.telefono),
    departamento: validateZonePart(form.departamento, 'El departamento'),
    ciudad: validateZonePart(form.ciudad, 'La ciudad'),
    barrio: validateZonePart(form.barrio, 'El barrio'),
    direccion: validateAddress(form.direccion),
    metodoPago: validatePaymentMethod(form.metodoPago),
  });
}

/** `motivo` del cambio de estado: opcional, hasta 500 caracteres (queda en el historial). */
export function validateStatusMotivo(value: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.motivo, 'El motivo');
}

/**
 * Formulario del cambio de estado: exige elegir un estado y valida el motivo si se escribió.
 *
 * `motivoCancelacion` (3-oct-2026) solo se valida cuando el destino es `CANCELADA`, y ahí es
 * **obligatorio**: es el dato que después permite contar por qué se perdeu cada pedido.
 */
export function validateStatusForm(
  estado: EstadoOrden | '',
  motivo: string,
  motivoCancelacion: MotivoCancelacion | '' = '',
): FormErrors {
  return formErrorsFromFields({
    estado: estado === '' ? 'Elige el estado al que se mueve la orden' : undefined,
    motivo: validateStatusMotivo(motivo),
    motivoCancelacion:
      estado === 'CANCELADA' && motivoCancelacion === ''
        ? 'Elegí por qué se cancela la orden'
        : undefined,
  });
}

/**
 * `guiaEnvioFotoUrl` — opcional, pero si viene tiene que ser una URL válida (el schema usa
 * `z.url()`). Se exige http/https a propósito: la única URL de guía que sirve es la que Meta puede
 * descargar para mandarle la foto al cliente.
 */
export function validateGuideUrl(value: string): string | undefined {
  const text = value.trim();
  if (text.length === 0) return undefined;

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return 'La dirección de la guía no es una URL válida';
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return 'La dirección de la guía tiene que empezar por http:// o https://';
  }

  return undefined;
}

/** `guiaEnvioNumero` — opcional, hasta 120 caracteres. */
export function validateGuideNumber(value: string): string | undefined {
  return tooLong(value, ORDER_LIMITS.guiaNumero, 'El número de guía');
}

/**
 * Frase para la pantalla tras intentar leer la etiqueta con Gemini (`GuideLabelReading` de
 * `@/types/orders`). Es una CONSULTA, no un error de la petición: la pantalla la pinta como aviso y el
 * admin escribe los datos a mano cuando no se pudo leer.
 */
export function describeGuideLabelReading(reading: {
  status: 'ok' | 'sin_gemini' | 'no_legible' | 'error';
  numeroGuia: string | null;
  transportadora: string | null;
}): string {
  if (reading.status === 'ok') {
    const partes = [reading.numeroGuia, reading.transportadora].filter(
      (valor): valor is string => Boolean(valor),
    );
    return `Leído de la foto: ${partes.join(' · ')}. Corrígelo si no cuadra.`;
  }

  if (reading.status === 'sin_gemini') {
    return 'No se pudo leer la etiqueta sola: carga la API key de Gemini en Integraciones (o escribe los datos a mano).';
  }

  if (reading.status === 'no_legible') {
    return 'No se pudo leer la etiqueta de esta foto: escribe los datos a mano.';
  }

  return 'La lectura de la etiqueta falló (Gemini no respondió): escribe los datos a mano.';
}

/**
 * `imageBase64` — el schema solo exige que no venga vacío; el tope real (5 MB) lo aplica el
 * almacenamiento del backend al detectar el MIME por firma binaria.
 *
 * Son DOS momentos distintos y por eso el segundo argumento:
 *  - **al ELEGIR el archivo** (`'pendiente'`) todavía no hay base64 —ni debe haberlo: comprobarlo aquí
 *    es justo lo que evita leer 12 MB para nada—, así que solo miran el nombre, el tope y el tipo;
 *  - **al ENVIAR** (`'leida'`, por defecto) el archivo ya se leyó: un base64 vacío significa que la
 *    lectura falló y hay que pedirle al usuario que elija la foto otra vez.
 *
 * ⚠️ El bug del 20-sep-2026: el diálogo llamaba a esta función al elegir el archivo (con `base64: ''`)
 * y la regla del base64 vacío rechazaba SIEMPRE la foto —«No se pudo leer el archivo: elige la foto
 * otra vez»—, así que la guía no se podía cargar nunca desde el panel.
 */
export function validateGuideFile(
  file: GuideFileState | null,
  lectura: 'pendiente' | 'leida' = 'leida',
): string | undefined {
  if (!file) return undefined;

  if (file.size > ORDER_LIMITS.guiaFotoBytes) {
    return `La foto de la guía no puede pesar más de ${Math.round(ORDER_LIMITS.guiaFotoBytes / (1024 * 1024))} MB`;
  }
  if (!file.mimeType.startsWith('image/')) return 'La guía tiene que ser una imagen (JPG o PNG)';
  if (lectura === 'leida' && file.base64.trim().length === 0) {
    return 'No se pudo leer el archivo: elige la foto otra vez';
  }

  return undefined;
}

/**
 * Formulario de la guía: hace falta una foto (subida o URL), como exige el `refine` del schema.
 * Si hay archivo, la URL deja de ser necesaria (el payload manda el archivo).
 */
export function validateGuideForm(form: GuideFormState): FormErrors {
  const url = validateGuideUrl(form.fotoUrl);
  const sinFoto =
    !form.archivo && form.fotoUrl.trim().length === 0
      ? 'Sube la foto de la guía o pega su dirección'
      : undefined;

  return formErrorsFromFields({
    guiaEnvioFotoUrl: url ?? sinFoto,
    imageBase64: validateGuideFile(form.archivo),
    guiaEnvioNumero: validateGuideNumber(form.numero),
  });
}
